import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon, type IconKey } from "@core/assets";
import { Avatar } from "../ui/Avatar";

export interface UserMenuItem {
  id: string;
  label: string;
  icon: IconKey;
  onAction: () => void;
  isDisabled?: boolean;
}

interface UserMenuProps {
  name: string;
  /** Already translated, display only. */
  roleLabel: string;
  items: readonly UserMenuItem[];
}

/**
 * The top bar's user menu (Figma "User menu"): avatar, name, role and a chevron; opened, a short
 * list of actions. Presentational: the caller supplies who the user is and what the items do, so
 * core learns nothing about sessions or routes. Built on React Aria's `MenuTrigger`, so keyboard
 * navigation, focus return and `aria-expanded` come for free.
 */
export function UserMenu({ name, roleLabel, items }: UserMenuProps) {
  const { t } = useTranslation("common");

  return (
    <MenuTrigger>
      <AriaButton
        aria-label={t("layout.user-menu")}
        className={
          "flex items-center gap-sm rounded-full py-xxs pr-sm pl-xxs text-left outline-none transition-colors " +
          "hover:bg-ink-100 pressed:bg-ink-100 " +
          "focus-visible:ring-2 focus-visible:ring-focus-ring"
        }
      >
        <Avatar name={name} size="sm" />
        <span className="hidden min-w-0 flex-col sm:flex">
          <span className="truncate text-sm-medium text-(--text-primary)">
            {name}
          </span>
          <span className="truncate text-xs-regular text-(--text-secondary)">
            {roleLabel}
          </span>
        </span>
        <Icon
          name="chevron-selector-vertical"
          size={16}
          className="shrink-0 text-(--text-secondary)"
        />
      </AriaButton>

      <Popover
        placement="bottom end"
        className="min-w-52 overflow-hidden rounded-lg border border-(--border-default) bg-surface-card shadow-menu outline-none"
      >
        <Menu
          aria-label={t("layout.user-menu")}
          className="p-xxs outline-none"
          disabledKeys={items.filter((i) => i.isDisabled).map((i) => i.id)}
          onAction={(key) => {
            items.find((item) => item.id === key)?.onAction();
          }}
        >
          {items.map((item) => (
            <MenuItem
              key={item.id}
              id={item.id}
              className={
                "flex cursor-pointer items-center gap-sm rounded-md px-sm py-2.5 outline-none " +
                "text-sm-medium text-(--text-primary) " +
                "hover:bg-ink-50 focus:bg-ink-50 " +
                "disabled:cursor-not-allowed disabled:text-(--text-disabled)"
              }
            >
              <Icon name={item.icon} size={18} className="shrink-0" />
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
